import { useMemo, useState } from "react";

import {

  Box,

  InputAdornment,

  TextField,

  Typography,

} from "@mui/material";


import SearchIcon from "@mui/icons-material/Search";


import { useTranslation } from "react-i18next";

import type { CatalogLegendItem } from "../../utils/partsCatalogUtils";

import type { RagImageItem } from "../../types/ragChatTypes";

import { RagMoreDiagramsCarousel } from "./RagMoreDiagramsCarousel";

import { RagImageLightbox } from "./RagImageLightbox";



interface PartsCatalogViewProps {

  entries: CatalogLegendItem[];

  mainDiagram: RagImageItem | null;

  moreDiagrams: RagImageItem[];

  msgId: string;

  pageLabel: string;

}



/** Max height for the scrollable legend list on the right. */

const LIST_CONTENT_MAX_HEIGHT = { xs: 280, md: 360 };



const MAIN_DIAGRAM_MAX_H = { xs: 320, md: 420 };





function MainDiagramPanel({

  image,

  pageLabel,

  onOpen,

  tapToOpenLabel,

  zoomLabel,

}: {

  image: RagImageItem;

  pageLabel: string;

  onOpen: (src: string) => void;

  tapToOpenLabel: string;

  zoomLabel: string;

}) {

  const page = String(image.metadata?.page ?? "?");

  const src = image.path;

  if (!src) return null;



  return (

    <Box

      component="button"

      type="button"

      onClick={() => onOpen(src)}

      aria-label={`${zoomLabel} ${pageLabel}${page}`}

      sx={{

        position: "relative",

        display: "block",

        width: "100%",

        p: 0,

        border: "1px solid",

        borderColor: "divider",

        borderRadius: 1.5,

        overflow: "hidden",

        bgcolor: "#ffffff",

        cursor: "pointer",

        boxShadow: "0 1px 4px rgba(28,46,54,0.08)",

        "&:hover .main-diagram-img": { transform: "scale(1.01)" },

        "&:focus-visible": {

          outline: "2px solid",

          outlineColor: "primary.main",

          outlineOffset: 2,

        },

      }}

    >

      <Box

        component="img"

        className="main-diagram-img"

        src={src}

        alt=""

        sx={{

          display: "block",

          width: "100%",

          maxHeight: MAIN_DIAGRAM_MAX_H,

          objectFit: "contain",

          objectPosition: "center",

          transition: "transform 0.2s ease",

        }}

      />

      <Box

        aria-hidden

        sx={{

          position: "absolute",

          left: 0,

          right: 0,

          bottom: 0,

          height: 28,

          display: "flex",

          alignItems: "flex-end",

          px: 1,

          pb: 0.375,

          background:

            "linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.28) 65%, transparent 100%)",

          pointerEvents: "none",

        }}

      >

        <Typography

          component="span"

          sx={{ color: "common.white", fontSize: 12, fontWeight: 600, lineHeight: 1.2 }}

        >

          {tapToOpenLabel}

        </Typography>

      </Box>

    </Box>

  );

}



function LegendRow({

  item,

  isLast,

  pageLabel,

  onOpenThumb,

}: {

  item: CatalogLegendItem;

  isLast: boolean;

  pageLabel: string;

  onOpenThumb: (src: string, gallery: { src: string; label?: string }[]) => void;

}) {

  return (

    <Box

      sx={{

        display: "flex",

        alignItems: "flex-start",

        // Fixed number column + gap so all item text shares one left edge.
        gap: 1.5,

        px: 1.25,

        py: 0.625,

        borderBottom: isLast ? "none" : "1px solid",

        borderColor: "divider",

      }}

    >

      <Typography

        component="span"

        data-testid="parts-legend-number"

        sx={{

          // Wide enough for double-digit labels like "10." / "11."
          flex: "0 0 36px",

          width: 36,

          fontSize: 14,

          fontWeight: 700,

          lineHeight: 1.35,

          color: "text.primary",

          textAlign: "left",

          fontVariantNumeric: "tabular-nums",

        }}

      >

        {`${item.number}.`}

      </Typography>

      <Box sx={{ flex: 1, minWidth: 0 }} data-testid="parts-legend-content">

        <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.35, color: "text.primary" }}>

          {item.name}

        </Typography>

        {item.description && (

          <Typography sx={{ mt: 0.125, fontSize: 12, lineHeight: 1.35, color: "text.secondary" }}>

            {item.description}

          </Typography>

        )}

      </Box>

      {item.images.length > 0 && item.images[0]?.img.path && (

        <Box

          component="button"

          type="button"

          onClick={() =>

            onOpenThumb(

              item.images[0]!.img.path,

              item.images.map(({ img, page }) => ({

                src: img.path,

                label: `${pageLabel}${page}`,

              }))

            )

          }

          sx={{

            flexShrink: 0,

            width: 36,

            height: 36,

            p: 0,

            border: "1px solid",

            borderColor: "divider",

            borderRadius: 1,

            overflow: "hidden",

            cursor: "pointer",

            bgcolor: "#eef3f8",

          }}

        >

          <Box

            component="img"

            src={item.images[0]!.img.path}

            alt=""

            sx={{ width: "100%", height: "100%", objectFit: "cover" }}

          />

        </Box>

      )}

    </Box>

  );

}



export function PartsCatalogView({

  entries,

  mainDiagram,

  moreDiagrams,

  msgId,

  pageLabel,

}: PartsCatalogViewProps) {

  const { t } = useTranslation();

  const [filter, setFilter] = useState("");

  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  const [lightboxGallery, setLightboxGallery] = useState<{ src: string; label?: string }[]>([]);





  const filteredEntries = useMemo(() => {

    const q = filter.trim().toLowerCase();

    if (!q) return entries;

    return entries.filter((item) => {

      const haystack = `${item.number} ${item.name} ${item.description ?? ""}`.toLowerCase();

      return haystack.includes(q);

    });

  }, [filter, entries]);



  const openLightbox = (src: string, gallery?: { src: string; label?: string }[]) => {

    setLightboxSrc(src);

    setLightboxGallery(gallery ?? [{ src }]);

  };



  const tapToOpenLabel = t("opva.chatAssistanceTapToOpen");

  const zoomLabel = t("opva.chatAssistanceClickToEnlarge");



  const mainGallery = mainDiagram?.path

    ? [{ src: mainDiagram.path, label: `${pageLabel}${mainDiagram.metadata?.page ?? "?"}` }]

    : [];



  return (

    <Box sx={{ width: "100%" }}>

      <Box

        sx={{

          display: "flex",

          flexDirection: { xs: "column", md: "row" },

          gap: { xs: 1.5, md: 2 },

          alignItems: "flex-start",

        }}

      >

        {mainDiagram?.path && (

          <Box

            sx={{

              flex: { md: "0 0 42%" },

              minWidth: 0,

              maxWidth: { md: 440 },

              width: { xs: "100%", md: "auto" },

              alignSelf: "flex-start",

              height: "fit-content",

              flexShrink: 0,

            }}

          >

            <MainDiagramPanel

              image={mainDiagram}

              pageLabel={pageLabel}

              onOpen={(src) => openLightbox(src, mainGallery)}

              tapToOpenLabel={tapToOpenLabel}

              zoomLabel={zoomLabel}

            />

          </Box>

        )}



        <Box

          sx={{

            width: { xs: "100%", md: mainDiagram?.path ? "58%" : "100%" },

            minWidth: 0,

            flex: "1 1 auto",

            alignSelf: "flex-start",

            height: "fit-content",

            border: "1px solid",

            borderColor: "divider",

            borderRadius: 2,

            bgcolor: "background.paper",

          }}

        >

          <Box sx={{ px: 1.25, pt: 1, pb: 0.5 }}>

            <TextField

              size="small"

              fullWidth

              value={filter}

              onChange={(e) => setFilter(e.target.value)}

              placeholder={t("opva.chatAssistanceFilterParts")}

              InputProps={{

                startAdornment: (

                  <InputAdornment position="start">

                    <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />

                  </InputAdornment>

                ),

              }}

              sx={{

                "& .MuiOutlinedInput-root": {

                  borderRadius: 999,

                  bgcolor: "grey.50",

                  fontSize: 13,

                },

              }}

            />

          </Box>



          <Box

            sx={{

              px: 0.5,

              pb: 0.75,

              overflowY: "auto",

              maxHeight: LIST_CONTENT_MAX_HEIGHT,

              "&::-webkit-scrollbar": { width: 6 },

              "&::-webkit-scrollbar-thumb": {

                bgcolor: "grey.300",

                borderRadius: 3,

              },

            }}

          >

            {filteredEntries.length === 0 ? (

              <Typography sx={{ px: 1.25, py: 0.75, fontSize: 13, color: "text.secondary" }}>

                {t("opva.chatAssistanceNoMatchingParts")}

              </Typography>

            ) : (

              filteredEntries.map((item, index) => (

                <LegendRow

                  key={`${msgId}-legend-${item.number}`}

                  item={item}

                  isLast={index === filteredEntries.length - 1}

                  pageLabel={pageLabel}

                  onOpenThumb={openLightbox}

                />

              ))

            )}

          </Box>

        </Box>

      </Box>



      {moreDiagrams.length > 0 && (
        <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
          <RagMoreDiagramsCarousel
            images={moreDiagrams}
            msgId={msgId}
            pageLabel={pageLabel}
            showHeader
          />
        </Box>
      )}



      <RagImageLightbox

        src={lightboxSrc}

        gallery={lightboxGallery}

        onClose={() => setLightboxSrc(null)}

      />

    </Box>

  );

}


